<template>
  <div class="drp-wrapper" ref="wrapper">
    <!-- Trigger -->
    <div
      class="drp-trigger"
      :class="{ 'drp-trigger--error': invalid, 'drp-trigger--open': open }"
      @click="toggle"
      role="button"
      tabindex="0"
      @keydown.enter="toggle"
    >
      <font-awesome-icon
        :icon="['fas', 'calendar-days']"
        class="drp-cal-icon"
      />
      <span v-if="startDate && endDate" class="drp-value">{{
        displayValue
      }}</span>
      <span v-else class="drp-placeholder">Seleziona periodo</span>
    </div>
    <span v-if="invalid && errorMsg" class="drp-error-msg"
      >&#9888; {{ errorMsg }}</span
    >

    <!-- Popover -->
    <div v-if="open" class="drp-popover" @click.stop>
      <!-- Navigazione mese -->
      <div class="drp-nav">
        <button type="button" class="drp-nav-btn" @click="prevMonth">
          &#8249;
        </button>
        <span class="drp-month-label">{{ monthLabel }}</span>
        <button type="button" class="drp-nav-btn" @click="nextMonth">
          &#8250;
        </button>
      </div>

      <!-- Nomi giorni -->
      <div class="drp-day-names">
        <span v-for="n in dayNames" :key="n">{{ n }}</span>
      </div>

      <!-- Griglia giorni -->
      <div class="drp-grid">
        <div
          v-for="(cell, i) in calendarCells"
          :key="i"
          class="drp-cell"
          :class="cellClasses(cell)"
          @click="cell.day && onDayClick(cell.day)"
          @mouseenter="cell.day && (hoverDay = cell.day)"
          @mouseleave="hoverDay = null"
        >
          <span v-if="cell.day" class="drp-day-num">{{ cell.day }}</span>
          <span
            v-if="cell.day && hasQuoteDot(cell.day)"
            class="drp-dot"
            :class="{ 'drp-dot--overlap': isInOverlap(cell.day) }"
          ></span>
        </div>
      </div>

      <!-- Hint selezione -->
      <p
        v-if="selectionHint"
        class="drp-hint"
        :class="{
          'drp-hint--error': !canConfirm && pendingStart && pendingEnd,
        }"
      >
        {{ selectionHint }}
      </p>

      <!-- Input orari -->
      <div class="drp-times">
        <label class="drp-time-label">
          <span>Inizio</span>
          <input type="time" v-model="startTime" @click.stop />
        </label>
        <span class="drp-time-sep">&#8594;</span>
        <label class="drp-time-label">
          <span>Fine</span>
          <input type="time" v-model="endTime" @click.stop />
        </label>
      </div>

      <!-- Legenda -->
      <div class="drp-legend">
        <span class="drp-legend-item">
          <span class="drp-dot drp-dot--quote"></span> Altri preventivi
        </span>
        <span class="drp-legend-item">
          <span class="drp-dot drp-dot--overlap"></span> Sovrapposizione
        </span>
      </div>

      <!-- Azioni -->
      <div class="drp-actions">
        <button type="button" class="drp-btn-clear" @click="clear">
          Cancella
        </button>
        <button
          type="button"
          class="drp-btn-confirm"
          :disabled="!canConfirm"
          @click="confirm"
        >
          Conferma
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DateRangePicker",
  props: {
    startDate: { type: String, default: "" },
    endDate: { type: String, default: "" },
    allQuotes: { type: Array, default: () => [] },
    currentQuoteId: { type: [Number, String], default: null },
    invalid: { type: Boolean, default: false },
    errorMsg: { type: String, default: "" },
  },
  emits: ["update:startDate", "update:endDate"],
  data() {
    return {
      open: false,
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
      pendingStart: null,
      pendingEnd: null,
      startTime: "09:00",
      endTime: "18:00",
      hoverDay: null,
      dayNames: ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"],
    };
  },
  computed: {
    monthLabel() {
      return new Date(this.viewYear, this.viewMonth, 1).toLocaleString(
        "it-IT",
        {
          month: "long",
          year: "numeric",
        }
      );
    },
    calendarCells() {
      const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay();
      const offset = (firstDay + 6) % 7;
      const days = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < offset; i++) cells.push({ day: null });
      for (let d = 1; d <= days; d++) cells.push({ day: d });
      return cells;
    },
    canConfirm() {
      if (!this.pendingStart || !this.pendingEnd) return false;
      if (
        this.isSameDay(this.pendingStart, this.pendingEnd) &&
        this.endTime <= this.startTime
      )
        return false;
      return true;
    },
    selectionHint() {
      if (!this.pendingStart) return "Clicca un giorno per la data di inizio";
      if (!this.pendingEnd) return "Ora clicca la data di fine";
      if (
        this.isSameDay(this.pendingStart, this.pendingEnd) &&
        this.endTime <= this.startTime
      ) {
        return "⚠ Stesso giorno: l'orario di fine deve essere successivo a quello di inizio";
      }
      return null;
    },
    displayValue() {
      const fmt = (str) => {
        if (!str) return "—";
        const [date, time] = str.split("T");
        const [y, m, d] = date.split("-");
        return `${d}/${m}/${y}${time ? " " + time : ""}`;
      };
      return `${fmt(this.startDate)} → ${fmt(this.endDate)}`;
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onClickOutside);
  },
  methods: {
    toggle() {
      if (this.open) {
        this.open = false;
        return;
      }
      if (this.startDate) {
        const [datePart, timePart] = this.startDate.split("T");
        const d = new Date(datePart);
        this.pendingStart = new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate()
        );
        this.startTime = timePart || "09:00";
        this.viewYear = d.getFullYear();
        this.viewMonth = d.getMonth();
      } else {
        this.pendingStart = null;
        this.startTime = "09:00";
      }
      if (this.endDate) {
        const [datePart, timePart] = this.endDate.split("T");
        const d = new Date(datePart);
        this.pendingEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        this.endTime = timePart || "18:00";
      } else {
        this.pendingEnd = null;
        this.endTime = "18:00";
      }
      this.open = true;
    },
    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },
    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },
    onDayClick(day) {
      const clicked = new Date(this.viewYear, this.viewMonth, day);
      if (!this.pendingStart || this.pendingEnd) {
        this.pendingStart = clicked;
        this.pendingEnd = null;
      } else if (
        clicked < this.pendingStart &&
        !this.isSameDay(clicked, this.pendingStart)
      ) {
        // Clic prima dell'inizio → ricomincia la selezione da questo giorno
        this.pendingStart = clicked;
        this.pendingEnd = null;
      } else {
        this.pendingEnd = clicked;
      }
    },
    confirm() {
      if (!this.canConfirm) return;
      const fmt = (date, time) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}T${time}`;
      };
      this.$emit("update:startDate", fmt(this.pendingStart, this.startTime));
      this.$emit("update:endDate", fmt(this.pendingEnd, this.endTime));
      this.open = false;
    },
    clear() {
      this.pendingStart = null;
      this.pendingEnd = null;
    },
    cellClasses(cell) {
      if (!cell.day) return ["drp-cell--empty"];
      const classes = [];
      const cellDate = new Date(this.viewYear, this.viewMonth, cell.day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (cellDate.getTime() === today.getTime())
        classes.push("drp-cell--today");
      if (this.pendingStart && this.isSameDay(cellDate, this.pendingStart))
        classes.push("drp-cell--start");
      if (this.pendingEnd && this.isSameDay(cellDate, this.pendingEnd))
        classes.push("drp-cell--end");
      const rangeEnd = this.pendingEnd
        ? this.pendingEnd
        : this.hoverDay && this.pendingStart
        ? new Date(this.viewYear, this.viewMonth, this.hoverDay)
        : null;
      if (this.pendingStart && rangeEnd) {
        const [rs, re] =
          this.pendingStart <= rangeEnd
            ? [this.pendingStart, rangeEnd]
            : [rangeEnd, this.pendingStart];
        if (cellDate > rs && cellDate < re) {
          classes.push(
            this.pendingEnd ? "drp-cell--in-range" : "drp-cell--hover-range"
          );
        }
      }
      return classes;
    },
    hasQuoteDot(day) {
      const cellDate = new Date(this.viewYear, this.viewMonth, day);
      const cellStart = new Date(cellDate);
      cellStart.setHours(0, 0, 0, 0);
      const cellEnd = new Date(cellDate);
      cellEnd.setHours(23, 59, 59, 999);
      return this.allQuotes.some(
        (q) =>
          String(q.id) !== String(this.currentQuoteId) &&
          q.rentStartDate &&
          q.rentEndDate &&
          new Date(q.rentStartDate) <= cellEnd &&
          new Date(q.rentEndDate) >= cellStart
      );
    },
    isInOverlap(day) {
      if (!this.pendingStart) return false;
      const cellDate = new Date(this.viewYear, this.viewMonth, day);
      const rangeEnd = this.pendingEnd
        ? this.pendingEnd
        : this.hoverDay
        ? new Date(this.viewYear, this.viewMonth, this.hoverDay)
        : null;
      if (!rangeEnd) return false;
      const [rs, re] =
        this.pendingStart <= rangeEnd
          ? [this.pendingStart, rangeEnd]
          : [rangeEnd, this.pendingStart];
      return cellDate >= rs && cellDate <= re && this.hasQuoteDot(day);
    },
    isSameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },
    onClickOutside(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.open = false;
      }
    },
  },
};
</script>

<style scoped>
.drp-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.drp-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  font-size: 12px;
  line-height: 1.6;
  width: 350px;
  box-sizing: border-box;
  user-select: none;
  transition: border-color 0.15s;
}

.drp-trigger:hover,
.drp-trigger--open {
  border-color: #9ec44a;
}

.drp-trigger--error {
  border-color: #e53e3e;
  background-color: #fff5f5;
}

.drp-cal-icon {
  color: #9ec44a;
  flex-shrink: 0;
}

.drp-placeholder {
  color: #757575;
}

.drp-value {
  color: #333;
}

.drp-error-msg {
  font-size: 12px;
  color: #e53e3e;
  margin-top: 4px;
  display: block;
}

/* Popover */
.drp-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  width: 300px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.drp-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.drp-month-label {
  font-weight: 600;
  font-size: 14px;
  text-transform: capitalize;
}

.drp-nav-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px 8px;
  font-size: 16px;
  line-height: 1.4;
}

/* Griglia */
.drp-day-names {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}

.drp-day-names span {
  text-align: center;
  font-size: 11px;
  color: #999;
  font-weight: 600;
  padding: 2px 0;
}

.drp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.drp-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 2px 3px;
  border-radius: 4px;
  cursor: pointer;
  min-height: 34px;
  transition: background 0.1s;
}

.drp-cell--empty {
  cursor: default;
}

.drp-cell:not(.drp-cell--empty):hover {
  background: #f0f7e6;
}

.drp-cell--today .drp-day-num {
  font-weight: 700;
  color: #9ec44a;
}

.drp-cell--start,
.drp-cell--end {
  background: #9ec44a !important;
  border-radius: 4px;
}

.drp-cell--start .drp-day-num,
.drp-cell--end .drp-day-num {
  color: #fff;
}

.drp-cell--in-range {
  background: #e8f5d0;
  border-radius: 0;
}

.drp-cell--hover-range {
  background: #f0f7e6;
  border-radius: 0;
}

.drp-day-num {
  font-size: 12px;
  line-height: 1;
}

.drp-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #bbb;
  margin-top: 2px;
  display: inline-block;
  flex-shrink: 0;
}

.drp-dot--overlap {
  background: #e53e3e;
}

.drp-dot--quote {
  background: #bbb;
}

/* Hint */
.drp-hint {
  font-size: 11px;
  color: #888;
  margin: 6px 0 0;
  text-align: center;
}

.drp-hint--error {
  color: #e53e3e;
  font-weight: 500;
}

/* Orari */
.drp-times {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin: 10px 0 8px;
  justify-content: center;
}

.drp-time-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #666;
}

.drp-time-label input[type="time"] {
  padding: 4px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  width: 90px;
}

.drp-time-sep {
  font-weight: bold;
  color: #9ec44a;
  margin-bottom: 6px;
}

/* Legenda */
.drp-legend {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
}

.drp-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
}

/* Azioni */
.drp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #eee;
  padding-top: 8px;
}

.drp-btn-clear {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
}

.drp-btn-clear:hover {
  background: #f5f5f5;
}

.drp-btn-confirm {
  background: #9ec44a;
  border: none;
  border-radius: 4px;
  padding: 4px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #fff;
  font-weight: 500;
}

.drp-btn-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.drp-btn-confirm:not(:disabled):hover {
  background: #89b039;
}

@media (max-width: 505px) {
  .drp-trigger {
    width: 100% !important;
  }
}
</style>
